import React, { useState, useEffect } from 'react';
import { Plus, Search, Building2, Pencil, Trash2, RotateCcw, ArrowRight, Eye } from 'lucide-react';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import BackButton from '../../../components/admin/BackButton';
import CreateClientModal from '../../../components/admin/crm/CreateClientModal';
import EditClientModal from '../../../components/admin/crm/EditClientModal';
import PromoteCounterpartyModal from '../../../components/admin/PromoteCounterpartyModal';
import AlsoBadge from '../../../components/admin/AlsoBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Clients = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [promoteSource, setPromoteSource] = useState(null);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20 });
  const [totalCount, setTotalCount] = useState(0);
  const { toast } = useToast();
  const navigate = useNavigate();

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchClients();
    fetchCount();
  }, [pagination, searchTerm]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const searchParam = searchTerm ? `&search=${encodeURIComponent(searchTerm)}` : '';
      const response = await fetch(
        `${API_URL}/api/crm/clients?skip=${pagination.skip}&limit=${pagination.limit}${searchParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch clients');

      const data = await response.json();
      setClients(data);
    } catch (error) {
      console.error('Error fetching clients:', error);
      toast({
        title: 'Error',
        description: 'Failed to load clients',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCount = async () => {
    try {
      const searchParam = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const response = await fetch(
        `${API_URL}/api/crm/clients/count${searchParam}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch count');

      const data = await response.json();
      setTotalCount(data.count);
    } catch (error) {
      console.error('Error fetching count:', error);
    }
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;

    try {
      const response = await fetch(`${API_URL}/api/crm/clients/${clientId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete client');

      toast({
        title: 'Success',
        description: 'Client deleted successfully'
      });
      fetchClients();
      fetchCount();
    } catch (error) {
      console.error('Error deleting client:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete client',
        variant: 'destructive'
      });
    }
  };

  const handleRestore = async (clientId) => {
    try {
      const response = await fetch(`${API_URL}/api/crm/clients/${clientId}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to restore client');

      toast({
        title: 'Success',
        description: 'Client restored successfully'
      });
      fetchClients();
    } catch (error) {
      console.error('Error restoring client:', error);
      toast({
        title: 'Error',
        description: 'Failed to restore client',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (client) => {
    setSelectedClient(client);
    setIsEditModalOpen(true);
  };

  const handleNextPage = () => {
    if (pagination.skip + pagination.limit < totalCount) {
      setPagination({ ...pagination, skip: pagination.skip + pagination.limit });
    }
  };

  const handlePrevPage = () => {
    if (pagination.skip > 0) {
      setPagination({ ...pagination, skip: Math.max(0, pagination.skip - pagination.limit) });
    }
  };

  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;
  const totalPages = Math.ceil(totalCount / pagination.limit);

  return (
    <div>
      <BackButton />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-[#00B4D8] bg-opacity-10 rounded-lg">
              <Building2 className="h-6 w-6 text-[#00B4D8]" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-[#2B2D42]">Clients</h2>
              <p className="text-sm text-[#6C757D]">Manage your client database</p>
            </div>
          </div>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-[#00B4D8] hover:bg-[#0096B8]"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#6C757D]" />
            <input
              type="text"
              placeholder="Search clients by company name, contact person, or email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPagination({ ...pagination, skip: 0 });
              }}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent"
            />
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Total Clients</p>
              <p className="text-3xl font-bold text-[#2B2D42]">{totalCount}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Current Page</p>
              <p className="text-3xl font-bold text-[#2B2D42]">
                {currentPage} / {totalPages || 1}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-[#6C757D] mb-1">Showing</p>
              <p className="text-3xl font-bold text-[#2B2D42]">{clients.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Clients List */}
      {loading ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">Loading clients...</p>
          </CardContent>
        </Card>
      ) : clients.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-[#6C757D]">
              {searchTerm ? 'No clients found matching your search.' : 'No clients yet. Add your first client!'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {clients.map((client) => (
            <Card key={client.id} className={client.is_deleted ? 'opacity-50' : ''}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-[#2B2D42]">
                        {client.company_name}
                      </h3>
                      {client.is_deleted && (
                        <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                          Deleted
                        </span>
                      )}
                      <AlsoBadge module="clients" recordId={client.id} current="client" />
                      {client.tags && client.tags.length > 0 && (
                        <div className="flex gap-1">
                          {client.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 text-xs bg-[#E0F2FE] text-[#00B4D8] rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm text-[#6C757D]">
                      <div>
                        <span className="font-medium">Contact:</span> {client.contact_person}
                      </div>
                      <div>
                        <span className="font-medium">Email:</span> {client.email}
                      </div>
                      {client.phone && (
                        <div>
                          <span className="font-medium">Phone:</span> {client.phone}
                        </div>
                      )}
                      {client.tax_id && (
                        <div>
                          <span className="font-medium">Tax ID:</span> {client.tax_id}
                        </div>
                      )}
                      {client.industry && (
                        <div>
                          <span className="font-medium">Industry:</span> {client.industry}
                        </div>
                      )}
                      {client.country && (
                        <div>
                          <span className="font-medium">Country:</span> {client.country}
                        </div>
                      )}
                    </div>
                    {client.notes && (
                      <div className="mt-3 text-sm text-[#6C757D]">
                        <span className="font-medium">Notes:</span> {client.notes}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {client.is_deleted ? (
                      <Button
                        onClick={() => handleRestore(client.id)}
                        variant="outline"
                        size="sm"
                      >
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Restore
                      </Button>
                    ) : (
                      <>
                        <Button
                          onClick={() => navigate(`/admin/crm/clients/${client.id}`)}
                          variant="outline"
                          size="sm"
                          className="text-[#00B4D8] hover:bg-cyan-50 border-cyan-300"
                          title="Open 360° client view (invoices, opportunities, activity)"
                          data-testid={`open-client-360-${client.id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => setPromoteSource({
                            prefixed_id: `client:${client.id}`,
                            name: client.company_name,
                            source: 'client',
                          })}
                          variant="outline"
                          size="sm"
                          className="text-[#FF6B35] hover:bg-orange-50 border-orange-300"
                          title="Promote to Supplier / Partner / Customer / Contact / Lead"
                          data-testid={`promote-client-${client.id}`}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleEdit(client)}
                          variant="outline"
                          size="sm"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => handleDelete(client.id)}
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalCount > pagination.limit && (
        <div className="mt-6 flex items-center justify-center space-x-4">
          <Button
            onClick={handlePrevPage}
            disabled={pagination.skip === 0}
            variant="outline"
          >
            Previous
          </Button>
          <span className="text-sm text-[#6C757D]">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            onClick={handleNextPage}
            disabled={pagination.skip + pagination.limit >= totalCount}
            variant="outline"
          >
            Next
          </Button>
        </div>
      )}

      {/* Modals */}
      <CreateClientModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          fetchClients();
          fetchCount();
        }}
      />

      <PromoteCounterpartyModal
        open={!!promoteSource}
        onClose={() => setPromoteSource(null)}
        source={promoteSource}
        onPromoted={() => fetchClients()}
      />

      {selectedClient && (
        <EditClientModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedClient(null);
          }}
          client={selectedClient}
          onSuccess={() => {
            setIsEditModalOpen(false);
            setSelectedClient(null);
            fetchClients();
          }}
        />
      )}
    </div>
  );
};

export default Clients;
